import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { Button, DotsVertical, EmptyState, Menu, Pagination, SearchLg, Table, Tabs, TextInput } from '@opendate-app/setlist';
import GivingConfirmModal from './GivingConfirmModal';

const axios = require('axios').default;

const ALL_SCOPE = 'all';
const PER_PAGE = 50;

function TagsManager({ csrfToken, team, scopes = [], canManageTags = false }) {
  const teamId = team && team.id;
  const [scope, setScope] = useState(ALL_SCOPE);
  const [search, setSearch] = useState('');
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [mergeTargetId, setMergeTargetId] = useState(null);
  const [confirmState, setConfirmState] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    if (csrfToken) {
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    }
  }, [csrfToken]);

  const fetchTags = useCallback(async () => {
    if (!teamId) return;
    try {
      setLoading(true);
      const response = await axios.get(`/teams/${teamId}/crm/tags.json`, {
        params: {
          scope: scope === ALL_SCOPE ? undefined : scope,
          name: search || undefined,
          paginated: true,
          page: currentPage,
          per_page: PER_PAGE,
        },
      });
      const data = response.data || {};
      const pagination = data.pagination || {};
      setTags(data.tags || []);
      setTotalPages(pagination.total_pages || 1);
    } catch (e) {
      toast.error('Failed to load tags.');
    } finally {
      setLoading(false);
    }
  }, [teamId, scope, search, currentPage]);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  useEffect(() => {
    setSelectedIds([]);
    setMergeTargetId(null);
    setCurrentPage(1);
  }, [scope, search]);

  const startRename = (tag) => {
    setEditingId(tag.id);
    setEditingName(tag.name);
  };

  const saveRename = async (tag) => {
    const name = editingName.trim();
    if (!name || name === tag.name) {
      setEditingId(null);
      return;
    }
    try {
      await axios.patch(`/teams/${teamId}/crm/tags/${tag.id}.json`, { tag: { name } });
      setEditingId(null);
      fetchTags();
    } catch (e) {
      const messages = e.response && e.response.data;
      toast.error(Array.isArray(messages) ? messages.join(', ') : 'Failed to rename tag.');
    }
  };

  const runConfirm = async () => {
    if (!confirmState) return;
    const { onConfirm } = confirmState;
    setConfirmState(null);
    await onConfirm();
  };

  const deleteTag = (tag) => {
    const usage = tag.tagged_items_count
      ? ` It is applied to ${tag.tagged_items_count} record(s), which will be untagged.`
      : '';
    setConfirmState({
      title: 'Delete tag',
      body: `Delete the tag "${tag.name}"?${usage}`,
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: async () => {
        try {
          await axios.delete(`/teams/${teamId}/crm/tags/${tag.id}.json`);
          fetchTags();
        } catch (e) {
          toast.error('Failed to delete tag.');
        }
      },
    });
  };

  const bulkDelete = () => {
    setConfirmState({
      title: 'Delete tags',
      body: `Delete ${selectedIds.length} selected tag(s)? Records will be untagged.`,
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: async () => {
        try {
          const res = await axios.delete(`/teams/${teamId}/crm/tags/bulk_destroy.json`, { params: { ids: selectedIds } });
          setSelectedIds([]);
          if (res.status === 202) {
            toast.info('Deleting tags in the background. This may take a moment.');
          } else {
            fetchTags();
          }
        } catch (e) {
          toast.error('Failed to delete tags.');
        }
      },
    });
  };

  const mergeSelected = () => {
    const target = mergeTargetId || selectedIds[0];
    const sourceIds = selectedIds.filter((id) => id !== target);
    if (sourceIds.length === 0) return;
    const targetTag = tags.find((t) => t.id === target);
    if (!targetTag) return;
    setConfirmState({
      title: 'Merge tags',
      body: `Merge ${sourceIds.length} tag(s) into "${targetTag.name}"? The other tags will be deleted and their records re-tagged.`,
      confirmLabel: 'Merge',
      danger: false,
      onConfirm: async () => {
        try {
          const res = await axios.post(`/teams/${teamId}/crm/tags/merge.json`, { source_ids: sourceIds, target_id: target });
          setSelectedIds([]);
          setMergeTargetId(null);
          if (res.status === 202) {
            toast.info('Merging tags in the background. This may take a moment.');
          } else {
            fetchTags();
          }
        } catch (e) {
          toast.error('Failed to merge tags.');
        }
      },
    });
  };

  const columns = [
    {
      key: 'name',
      header: 'Tag',
      primary: true,
      render: (tag) => (editingId === tag.id ? (
        <TextInput
          aria-label={`Rename ${tag.name}`}
          value={editingName}
          autoFocus
          onChange={(e) => setEditingName(e.target.value)}
          onBlur={() => saveRename(tag)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') saveRename(tag);
            if (e.key === 'Escape') setEditingId(null);
          }}
        />
      ) : tag.name),
    },
    { key: 'count', header: 'Used by', render: (tag) => tag.tagged_items_count },
    ...(canManageTags ? [{
      key: 'actions',
      header: <span className="sr-only">Actions</span>,
      align: 'right',
      width: '1%',
      render: (tag) => (
        <Menu
          placement="bottom-end"
          items={[
            { label: 'Rename', onClick: () => startRename(tag) },
            { label: 'Delete', destructive: true, onClick: () => deleteTag(tag) },
          ]}
          trigger={<Button variant="tertiary" size="sm" icon={DotsVertical} aria-label={`More actions for ${tag.name}`} />}
        />
      ),
    }] : []),
  ];

  const selectedTags = tags.filter((t) => selectedIds.includes(t.id));
  const selectedScopes = [...new Set(selectedTags.map((t) => t.scope))];
  const canMerge = selectedScopes.length === 1;

  return (
    <>
      <div className="crm-tags-toolbar d-flex flex-wrap align-items-center gap-2 mt-3 mb-3">
        <Tabs
          variant="button-border"
          size="sm"
          overflow
          aria-label="Tag type"
          value={scope}
          onChange={setScope}
          items={[
            { value: ALL_SCOPE, label: 'All' },
            ...scopes.map((s) => ({ value: s.value, label: s.label })),
          ]}
        />
        <div style={{ maxWidth: 240, marginLeft: 'auto' }}>
          <TextInput
            leadingIcon={SearchLg}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search tags"
            aria-label="Search tags"
          />
        </div>
      </div>

      {canManageTags && selectedIds.length > 0 && (
        <div
          className="crm-tags-bulkbar mb-3 px-3 py-2 rounded"
          style={{ background: 'var(--sl-bg-secondary)', border: '1px solid var(--sl-border-secondary)' }}
        >
          <span className="small" style={{ color: 'var(--sl-text-tertiary)' }}>{selectedIds.length} selected</span>
          <span className="crm-tags-bulkbar__delete">
            <Button variant="destructive" size="sm" onClick={bulkDelete}>
              Delete selected
            </Button>
          </span>
          {selectedIds.length >= 2 && (canMerge ? (
            <>
              <span className="small crm-tags-bulkbar__merge-label" style={{ color: 'var(--sl-text-tertiary)' }}>Merge into:</span>
              <select
                className="form-select crm-merge-select"
                style={{ maxWidth: 220 }}
                value={mergeTargetId || selectedIds[0]}
                onChange={(e) => setMergeTargetId(Number(e.target.value))}
              >
                {selectedIds.map((id) => {
                  const tag = tags.find((t) => t.id === id);
                  return tag ? <option key={id} value={id}>{tag.name}</option> : null;
                })}
              </select>
              <Button variant="primary" size="sm" onClick={mergeSelected}>
                Merge
              </Button>
            </>
          ) : (
            <span className="small crm-tags-bulkbar__merge-label" style={{ color: 'var(--sl-text-tertiary)' }}>
              Select tags of one type to merge.
            </span>
          ))}
        </div>
      )}

      <div className="crm-table-card">
        <Table
          columns={columns}
          data={tags}
          getRowKey={(tag) => tag.id}
          loading={loading}
          empty={<EmptyState size="sm" icon={SearchLg} title="No tags found." />}
          selectable={canManageTags}
          selectedKeys={selectedIds}
          onSelectionChange={setSelectedIds}
        />
      </div>

      {totalPages > 1 && (
        <div className="mt-3">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(page) => { if (!loading) setCurrentPage(page); }}
          />
        </div>
      )}

      <GivingConfirmModal
        open={!!confirmState}
        title={confirmState && confirmState.title}
        body={confirmState && confirmState.body}
        confirmLabel={confirmState && confirmState.confirmLabel}
        danger={confirmState && confirmState.danger}
        onConfirm={runConfirm}
        onCancel={() => setConfirmState(null)}
      />
    </>
  );
}

TagsManager.propTypes = {
  csrfToken: PropTypes.string,
  team: PropTypes.object,
  scopes: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  })),
  canManageTags: PropTypes.bool,
};

export default TagsManager;
